import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthPage } from "./pages/AuthPage";
import { Layout } from "./components/Layout";
import { Dashboard } from "./pages/Dashboard";
import { ChannelsPage } from "./pages/ChannelsPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { NettingPage } from "./pages/NettingPage";
import { DeficitHistoryPage } from "./pages/DeficitHistoryPage";
import { SettingsPage } from "./pages/SettingsPage";
import api from "./api/axiosInstance";
import { getSessionId } from "./utils/session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('MULTILATERAL_NETTING');

  // Fetch config whenever authenticated to know if netting is active
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchAlgo = async () => {
      try {
        const r = await api.get(`${API}/config`);
        setSelectedAlgorithm(r.data.selected_algorithm || 'MULTILATERAL_NETTING');
      } catch (_) {}
    };
    fetchAlgo();
    // Re-check every 5s so nav updates when user changes algorithm in Settings
    const id = setInterval(fetchAlgo, 5000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  useEffect(() => {
    // Connect to WebSocket for real-time updates
    if (isAuthenticated) {
      const sessionId = getSessionId() || '';
      const wsBase = window.location.protocol === 'https:'
        ? `wss://${window.location.host}/ws`
        : `ws://${window.location.host}/ws`;
      const ws = new WebSocket(`${wsBase}?session_id=${encodeURIComponent(sessionId)}`);

      ws.onopen = () => {
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          JSON.parse(event.data);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        setWsConnected(false);
      };

      // Heartbeat to keep connection alive
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      return () => {
        clearInterval(heartbeat);
        ws.close();
      };
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <AuthPage onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Layout nettingEnabled={selectedAlgorithm === 'MULTILATERAL_NETTING'}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/queues" element={<Navigate to="/channels" replace />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/netting" element={<NettingPage />} />
            <Route path="/deficit-history" element={<DeficitHistoryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
